/**
 * "Recognised and collected, six months" — `mockups/studio/money.html`'s last section.
 *
 * `StudioMoneyView.recognised_vs_collected`, one row per month, both figures already
 * USD-equivalent (`readmodels/recognised-vs-collected.ts`).
 *
 * **The Gap column and the six-month total are not here.** The mockup prints a third
 * column of `recognised − collected` and a footer summing all three, and both are
 * arithmetic on a `Money` that no field carries — `RecognisedCollectedMonthSchema` is
 * `{ period, recognised, collected }` and nothing else. What this does instead is
 * *mark* the months that have a gap: `compare` is `money.ts`'s own predicate, so a
 * month where less came in than went on an invoice is tinted and says so, without
 * this file inventing the number. The report asks for `gap` on the row and a `totals`
 * object on the view; the mockup's whole point is that column.
 */

import { compare, type StudioMoneyView } from '@fareast/shared'

import { Card, CardBody } from '../../../components/portal/Card.js'
import { Section, SectionHead } from '../../../components/portal/Layout.js'
import { TBody, THead, Table, Td, Th, Tr } from '../../../components/portal/Table.js'
import { Muted, Num } from '../../../components/portal/Text.js'
import { useFormat } from '../../../format/useFormat.js'

export type SixMonthsSectionProps = {
  readonly months: StudioMoneyView['recognised_vs_collected']
}

export function SixMonthsSection({ months }: SixMonthsSectionProps) {
  const format = useFormat()
  const span = months.length === 1 ? 'month' : `${String(months.length)} months`

  return (
    <Section>
      <SectionHead
        title={`Recognised and collected, ${span}`}
        note="What was invoiced against what actually arrived"
      />

      <Card>
        <CardBody tight scrollX>
          <Table>
            <THead>
              <Tr>
                <Th scope="col">Month</Th>
                <Th scope="col" right>
                  Recognised
                </Th>
                <Th scope="col" right>
                  Collected
                </Th>
                <Th scope="col">Settled</Th>
              </Tr>
            </THead>
            <TBody>
              {months.map((month) => {
                /* `compare` is `money.ts`'s, so this is a predicate rather than a
                   subtraction. −1 means less was collected than was invoiced. */
                const unsettled = compare(month.collected, month.recognised) === -1
                return (
                  <Tr key={month.period} variant={unsettled ? 'need' : undefined}>
                    <Td>{format.month(month.period)}</Td>
                    <Td right num>
                      <Num value={month.recognised} />
                    </Td>
                    <Td right num>
                      <Num value={month.collected} />
                    </Td>
                    <Td>{unsettled ? 'still short' : <Muted>in full</Muted>}</Td>
                  </Tr>
                )
              })}
            </TBody>
          </Table>
        </CardBody>
      </Card>
    </Section>
  )
}
